import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  LookingToJoin,
  SignInWithProvider,
  Text,
  Main,
} from '../../../components'
import { pickEmail, getOrganization } from './state'
import { JoinDefaultProps } from './types'
import { SSOProviders } from 'components/SSOProviders'
import { useAnalytics } from 'hooks/useAnalytics'
import { FormattedMessage } from 'react-intl'

export function Start({
  organizationInviteToken,
  joinOrigin,
  superEnabled,
}: JoinDefaultProps) {
  const dispatch = useDispatch()
  const track = useAnalytics()
  const organization = useSelector(getOrganization)

  const formattedAuthDomains = ((organization.authDomains as string) || '')
    .split(',')
    .map((domain) => `@${domain}`)
    .join(', ')

  const hasAutoJoin =
    organization.membersCanAutoJoinByDomain &&
    organization.authDomains &&
    organization.authDomains.length > 0

  const onPickEmail = () => dispatch(pickEmail())

  const onClick = (
    provider: SSOProviders,
    organizationInviteToken: string,
    callback: () => void
  ) => {
    switch (provider) {
      case SSOProviders.Google:
        track('ONBOARDING_JOIN_START_GOOGLE_SIGNUP', {
          from_open_invite_link: Boolean(organizationInviteToken),
        })
        break
      case SSOProviders.Slack:
        track('ONBOARDING_JOIN_START_SLACK_SIGNUP', {
          from_open_invite_link: Boolean(organizationInviteToken),
        })
        break
      default:
        throw new Error(`Unknown sign up method ${provider}`)
    }
    setTimeout(() => {
      callback()
    }, 300) // Delay redirection by 300ms - recommended by Segment
  }

  return (
    <Main.Container footer={<LookingToJoin superEnabled={superEnabled} />}>
      <Main.Title>
        <FormattedMessage
          id="boarding.organization.join.start.title"
          defaultMessage="Join the {product} team"
          values={{ product: superEnabled ? 'Super' : 'Slite' }}
        />
        <br />
        <span className="capitalize">{organization.domain}</span>
      </Main.Title>
      {!organizationInviteToken && hasAutoJoin > 0 && (
        <Text.Corpus
          className="center-text margin-vertical-12"
          style={{ alignSelf: 'center' }}
        >
          <FormattedMessage
            id="boarding.organization.join.start.autoJoin"
            defaultMessage="You can join without an invite if you have a {formattedAuthDomains} email address."
            values={{ formattedAuthDomains: <b>{formattedAuthDomains}</b> }}
          />
        </Text.Corpus>
      )}
      <div className="flexbox-column flexbox-inline ">
        <SignInWithProvider
          provider={SSOProviders.Google}
          rejoin={organization.domain}
          joinOrigin={joinOrigin}
          restrictToDomains={organization.authDomains || ''}
          className="margin-vertical-8"
          onClick={(callback) =>
            onClick(SSOProviders.Google, organizationInviteToken, callback)
          }
          organizationId={organization.id}
          organizationInviteToken={organizationInviteToken}
          superEnabled={superEnabled}
        />
        <SignInWithProvider
          provider={SSOProviders.Slack}
          rejoin={organization.domain}
          joinOrigin={joinOrigin}
          organizationId={organization.id}
          restrictToDomains={organization.authDomains || ''}
          className="margin-vertical-8"
          onClick={(callback) =>
            onClick(SSOProviders.Slack, organizationInviteToken, callback)
          }
          organizationInviteToken={organizationInviteToken}
          superEnabled={superEnabled}
        />
        <SignInWithProvider
          provider={SSOProviders.Apple}
          rejoin={organization.domain}
          joinOrigin={joinOrigin}
          organizationId={organization.id}
          restrictToDomains={organization.authDomains || ''}
          className="margin-vertical-8"
          onClick={(callback) =>
            onClick(SSOProviders.Apple, organizationInviteToken, callback)
          }
          organizationInviteToken={organizationInviteToken}
          superEnabled={superEnabled}
        />
      </div>
      <Text.SmallDetail
        className="margin-vertical-8"
        style={{ alignSelf: 'center' }}
      >
        <FormattedMessage
          id="boarding.organization.join.start.orSignUpWithEmail"
          defaultMessage="Or <link>sign up with your work email</link>"
          values={{
            link: (text: React.ReactNode) => (
              <Text.Action
                inline
                onClick={onPickEmail}
                data-test-id="startWithEmail"
              >
                {text}
              </Text.Action>
            ),
          }}
        />
      </Text.SmallDetail>
    </Main.Container>
  )
}
